import React from "react";
import { makeStyles } from "@material-ui/core/styles";

/* ── Keyframe-free shimmer using a CSS-only pseudo-element ──
   MUI v4's makeStyles doesn't support @keyframes natively, so
   the shimmer is a sliding linear-gradient on a ::after overlay
   driven by a CSS animation defined via a <style> tag injected
   once (see the SHIMMER_CSS constant below). */

const SHIMMER_CSS = `
@keyframes posCardShimmer {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
@keyframes posSkeletonFadeIn {
  0%   { opacity: 0; transform: translateY(8px); }
  100% { opacity: 1; transform: translateY(0); }
}
`;

// Inject the keyframes once globally (idempotent - checks before re-adding).
if (typeof document !== "undefined") {
  const id = "pos-card-shimmer-keyframes";
  if (!document.getElementById(id)) {
    const style = document.createElement("style");
    style.id = id;
    style.textContent = SHIMMER_CSS;
    document.head.appendChild(style);
  }
}

const useStyles = makeStyles((theme) => ({
  /* Outer wrapper matches the real ItemCard's aspect ratio, radius and border
     so the skeleton → real-card transition is completely seamless. */
  card: {
    position: "relative",
    width: "100%",
    aspectRatio: "4 / 3",
    borderRadius: 12,
    overflow: "hidden",
    background: "linear-gradient(145deg, #f1f5f9 0%, #e2e8f0 100%)",
    border: "1px solid rgba(226, 232, 240, 0.85)",
    [theme.breakpoints.down("xs")]: {
      borderRadius: 10,
    },
  },

  /* Faint icon circle (mirrors the real card's placeholder behind the photo) */
  iconCircle: {
    position: "absolute",
    top: "28%",
    left: "50%",
    transform: "translateX(-50%)",
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: "rgba(255, 255, 255, 0.55)",
    [theme.breakpoints.down("xs")]: {
      width: 36,
      height: 36,
    },
  },

  /* Bottom overlay matching the real card's name + price bar shape */
  bottomBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    minHeight: "42%",
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    background:
      "linear-gradient(180deg, rgba(148, 163, 184, 0) 0%, rgba(100, 116, 139, 0.18) 30%, rgba(71, 85, 105, 0.38) 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0.5, 1, 1),
    boxSizing: "border-box",
    gap: 5,
    [theme.breakpoints.down("xs")]: {
      borderBottomLeftRadius: 10,
      borderBottomRightRadius: 10,
      padding: theme.spacing(0.25, 0.75, 0.75),
    },
  },

  /* Price pill skeleton */
  pricePill: {
    width: 52,
    height: 18,
    borderRadius: 5,
    background: "rgba(255, 255, 255, 0.28)",
    [theme.breakpoints.down("xs")]: {
      width: 44,
      height: 15,
    },
  },

  /* Name line skeleton */
  nameLine: {
    width: "72%",
    height: 12,
    borderRadius: 6,
    background: "rgba(255, 255, 255, 0.22)",
    [theme.breakpoints.down("xs")]: {
      height: 10,
    },
  },

  /* The sliding shimmer overlay */
  shimmer: {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    borderRadius: "inherit",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)",
      animation: "posCardShimmer 1.6s ease-in-out infinite",
    },
  },
}));

/**
 * POS-specific item-card skeleton.
 * Matches the exact aspect ratio, border radius, and bottom-bar layout of the
 * real ItemCard so there's zero layout shift when data arrives.
 */
const PosItemSkeleton = () => {
  const classes = useStyles();
  return (
    <div className={classes.card}>
      <div className={classes.iconCircle} />
      <div className={classes.bottomBar}>
        <div className={classes.pricePill} />
        <div className={classes.nameLine} />
      </div>
      <div className={classes.shimmer} />
    </div>
  );
};

export default PosItemSkeleton;
